import { outcomeToStatusFilter } from '@flakiness/server/common/fql/extractor.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableBytes } from '@flakiness/shared/common/utils.js';
import { html, nothing, TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { api } from '../api.js';
import { DataColumn, DataTableProvider } from '../components/data-table.js';
import { renderDate, renderRelativeDate } from '../fragments.js';
import { PageRun } from '../page-run.js';
import { docsUrl } from '../platformUrl.js';

function createRunIdColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.WellformedRun, ''> {
  const runlinker = PageRun.linkifier(project, snapshotId);
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Run #`; },
    renderElement(run): TemplateResult {
      return html`
        <a href=${runlinker(run)}>
          #${run.runId}
        </a>
      `;
    },
  };
}

function createRunTitleColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.WellformedRun, ''> {
  const runlinker = PageRun.linkifier(project, snapshotId);
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`
      <h-box style="gap: var(--sl-spacing-2x-small)">
        <span>Title</span>
        <fk-help href=${docsUrl('/ci/naming-runs/')} style="font-size: var(--sl-font-size-x-small)">
          <p>A label that tells runs apart, like "Unit Tests" or "E2E Tests". It doesn't affect history or analytics.</p>
          <p>Set it with the <code>FLAKINESS_TITLE</code> environment variable in your CI job.</p>
        </fk-help>
      </h-box>
    `; },
    renderElement(run): TemplateResult {
      return html`
        <a href=${runlinker(run)}>
          ${run.title ?? nothing}
        </a>
      `;
    },
  };
}

function createRunEnvColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.WellformedRun, ''> {
  const runlinker = PageRun.linkifier(project, snapshotId);
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Run Environment`; },
    renderElement(run): TemplateResult {
      return html`
        <fk-runenvs
          style="margin: var(--sl-spacing-small) 0;"
          .run=${run}
          .runlinker=${runlinker}
        ></fk-runenvs>
      `;
    },
  };
}

function createTestOutcomesColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.WellformedRun, ''> {
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Test Results`; },
    renderElement(run): TemplateResult {
      if (!run)
        return html`<span style="margin: var(--sl-spacing-small) 0">Processing...</span>`;
      const linkifier = (outcome: WireTypes.Outcome) => PageRun.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: run.runId,
        query: `status:${outcomeToStatusFilter(outcome)}`,
        snapshotId,
      });
      return html`<outcomes-bar
        style="width: 100%;"
        .counts=${run.testStatsOutcomes}
        .linkifier=${linkifier}
      ></outcomes-bar>`;
    },
  };
}

function createRunStartDate(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.WellformedRun, ''> {
  const runlinker = PageRun.linkifier(project, snapshotId);
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Date & Time`; },
    renderElement(run): TemplateResult {
      const timestamp = run.startTimestamp;
      return html`
        <v-box style="gap: var(--sl-spacing-x-small);">
          <a href=${run ? runlinker(run) : nothing}>${timestamp ? renderRelativeDate(timestamp) : nothing}</a>
          <time-interval .ms=${run.duration}></time-interval>
        </v-box>
      `;
    },
  };
}

const COLUMN_SIZE: DataColumn<WireTypes.WellformedRun, ''> = {
  sortAxis: undefined,
  width: 'auto',
  renderHeader() { return html`Size`; },
  renderElement({ reportBytes, attachmentBytes }): TemplateResult {
    if (reportBytes === undefined && attachmentBytes === undefined)
      return html`${nothing}`;
    return html`
      <sl-tooltip hoist>
        <div slot=content>
          <div>Report: ${humanReadableBytes(reportBytes ?? 0)}</div>
          <div>Attachments: ${humanReadableBytes(attachmentBytes ?? 0)}</div>
        </div>
        <span>${humanReadableBytes((reportBytes ?? 0) + (attachmentBytes ?? 0))}</span>
      </sl-tooltip>
    `;
  },
};

const COLUMN_COMMIT: DataColumn<WireTypes.WellformedRun, ''> = {
  sortAxis: undefined,
  width: 'auto',
  renderHeader() { return html`Tested Commit`; },
  renderElement({ commitId, commit }): TemplateResult {
    if (!commit)
      return html`<span style="color: var(--sl-color-neutral-500); font-size: var(--sl-font-size-small)"><span style="font-family: var(--sl-font-mono)">${commitId.substring(0, 7)}</span> not found in the repository. Is it a local commit?</span>`;
    return html`<fk-commit .commit=${commit}></fk-commit>`;
  },
};

function createMalformedRunIdColumn(options: {
  orgSlug: string,
  projectSlug: string,
  snapshotId: WireTypes.DataSnapshotId,
}): DataColumn<WireTypes.MalformedRun, ''> {
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Run #`; },
    renderElement({ runId }): TemplateResult {
      const href = PageRun.url({
        orgSlug: options.orgSlug,
        projectSlug: options.projectSlug,
        reportId: runId,
        snapshotId: options.snapshotId,
      });
      return html`<a style=${styleMap({
          'padding': 'var(--sl-spacing-x-small) 0',
        })} href=${href}>#${runId}</a>`;
    },
  };
}

function createMalformedRunUpload(options: {
  orgSlug: string,
  projectSlug: string,
  snapshotId: WireTypes.DataSnapshotId,
}): DataColumn<WireTypes.MalformedRun, ''> {
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Upload Time`; },
    renderElement(run): TemplateResult {
      const timestamp = run.uploadTimestamp;
      const href = PageRun.url({
        orgSlug: options.orgSlug,
        projectSlug: options.projectSlug,
        reportId: run.runId,
        snapshotId: options.snapshotId,
      });

      return html`
        <a href=${href}>${timestamp ? renderDate(timestamp) : nothing}</a>
      `;
    },
  };
}

function createMalformedReportError(options: {
  orgSlug: string,
  projectSlug: string,
  snapshotId: WireTypes.DataSnapshotId,
}): DataColumn<WireTypes.MalformedRun, ''> {
  return {
    sortAxis: undefined,
    width: '1fr',
    renderHeader() { return html`Processing Error`; },
    renderElement({ runId, error }): TemplateResult {
      const href = PageRun.url({
        orgSlug: options.orgSlug,
        projectSlug: options.projectSlug,
        reportId: runId,
        snapshotId: options.snapshotId,
      });
      return html`<a href=${href} style=${styleMap({
        'white-space': 'nowrap',
        'overflow': 'hidden',
        'text-overflow': 'ellipsis',
      })}>${error}</a>`;
    },
  };
}

export function createReportsDataProvider(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataTableProvider<WireTypes.WellformedRun, ''> {
  return {
    columns: [
      createRunIdColumn(project, snapshotId),
      createRunTitleColumn(project, snapshotId),
      createRunEnvColumn(project, snapshotId),
      createRunStartDate(project, snapshotId),
      COLUMN_SIZE,
      COLUMN_COMMIT,
      createTestOutcomesColumn(project, snapshotId),
    ],
    defaultSortColumn: undefined,
    loader: (pageOptions, sortOptions, signal) => {
      return api.history.listWellformedRuns.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        pageOptions: pageOptions,
      }, { signal });
    }
  }
}

export function createMalformedReportsDataProvider(options: {
  orgSlug: string,
  projectSlug: string,
  snapshotId: WireTypes.DataSnapshotId,
}): DataTableProvider<WireTypes.MalformedRun, ''> {
  return {
    columns: [
      createMalformedRunIdColumn(options),
      createMalformedRunUpload(options),
      createMalformedReportError(options),
    ],
    defaultSortColumn: undefined,
    loader: (pageOptions, sortOptions, signal) => {
      return api.history.listMalformedRuns.GET({
        orgSlug: options.orgSlug,
        projectSlug: options.projectSlug,
        snapshotId: options.snapshotId,
        pageOptions: pageOptions,
      }, { signal });
    }
  }
}
